import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, switchMap } from 'rxjs';
import { HtmlContentIm, PdfGeneratorService } from './pdf-generator.service';

/**
 * Prints an agreement as PDF. Agreements are stored as editor (Quill) markup, so the document carries
 * the editor's own stylesheet - lists, alignment and indents then look the same as in the editor.
 */
@Injectable({ providedIn: 'root' })
export class AgreementPdfService {
  private readonly http = inject(HttpClient);
  private readonly pdfGeneratorService = inject(PdfGeneratorService);

  // Copied into the build from node_modules/quill/dist (angular.json). Without it the PDF still prints, only lists lose their markers.
  private readonly quillCss$ = this.http.get('assets/quill/quill.core.css', { responseType: 'text' }).pipe(
    catchError(() => of('')),
    shareReplay(1)
  );

  /** Builds the PDF and saves it in the browser under a name made of the given parts. */
  download(title: string, html: string, fileNameParts: (string | null | undefined)[] = []): Observable<void> {
    return this.generate(title, html).pipe(
      map((blob) => this.save(blob, this.fileName([title, ...fileNameParts])))
    );
  }

  generate(title: string, html: string): Observable<Blob> {
    return this.quillCss$.pipe(
      switchMap((quillCss) => {
        const content: HtmlContentIm = {
          body: this.document(title, html, quillCss),
          // Page numbers only: the footer font of the PDF service has no Polish letters ("ę" prints as "ÿ"),
          // and the title is the heading of the agreement anyway.
          footer: null,
          targetFileName: null,
          pagesTranslation: 'Strona',
          ofPagesTranslation: 'z',
        };
        return this.pdfGeneratorService.post(content);
      })
    );
  }

  private document(title: string, html: string, quillCss: string): string {
    return `<!DOCTYPE html>
<html lang="pl">
  <head>
    <meta charset="utf-8" />
    <title>${this.escape(title)}</title>
    <style>${quillCss}${AGREEMENT_PRINT_CSS}</style>
  </head>
  <body><div class="ql-editor agreement-document">${html}</div></body>
</html>`;
  }

  private save(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private fileName(parts: (string | null | undefined)[]): string {
    const name = parts
      .map((part) => (part ?? '').trim())
      .filter((part) => part.length > 0)
      .join('_')
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, '_');
    return `${name || 'umowa'}.pdf`;
  }

  private escape(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

/** Page look of a printed agreement; the editor's own .ql-editor rules are made print friendly. */
const AGREEMENT_PRINT_CSS = `
  body { margin: 0; color: #000; font-family: 'Liberation Sans', Arial, Helvetica, sans-serif; font-size: 11pt; }
  .ql-editor.agreement-document { height: auto; overflow: visible; padding: 0; line-height: 1.45; }
  .ql-editor.agreement-document h1 { font-size: 16pt; }
  .ql-editor.agreement-document h2 { font-size: 13pt; }
  .ql-editor.agreement-document h3 { font-size: 11.5pt; }
  .ql-editor.agreement-document table { page-break-inside: avoid; }
`;
